import { useEffect, useRef } from "react";
import { Crisp } from "crisp-sdk-web";
import { useUser } from "@clerk/react";
import { usePlan } from "../hooks/use-plan";

const CRISP_WEBSITE_ID = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Payment failed",
  canceled: "Canceled",
  unpaid: "Unpaid",
  incomplete: "Incomplete",
  incomplete_expired: "Incomplete (expired)",
  paused: "Paused",
};

export function CrispWidget() {
  const { user, isLoaded, isSignedIn } = useUser();
  const initializedRef = useRef(false);
  const { data: planInfo } = usePlan();

  useEffect(() => {
    if (!CRISP_WEBSITE_ID) return;
    if (!initializedRef.current) {
      Crisp.configure(CRISP_WEBSITE_ID);
      initializedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!CRISP_WEBSITE_ID || !initializedRef.current) return;
    if (!isLoaded) return;

    if (isSignedIn && user) {
      const email = user.primaryEmailAddress?.emailAddress;
      const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined;
      if (email) Crisp.user.setEmail(email);
      if (name) Crisp.user.setNickname(name);
    } else {
      try {
        Crisp.session.reset();
      } catch (err) {
        console.error("[CrispWidget] Failed to reset session:", err);
      }
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    if (!CRISP_WEBSITE_ID || !initializedRef.current) return;
    if (!isSignedIn || !planInfo) return;

    try {
      const rawStatus = planInfo.subscriptionStatus;
      const subscriptionStatusLabel = rawStatus
        ? (SUBSCRIPTION_STATUS_LABELS[rawStatus] ?? rawStatus)
        : "None";
      const planLabel =
        planInfo.plan.charAt(0).toUpperCase() + planInfo.plan.slice(1);
      const billingSummary = rawStatus
        ? `${planLabel} — ${planInfo.usageCount} jobs — ${subscriptionStatusLabel}`
        : `${planLabel} — ${planInfo.usageCount} jobs`;

      Crisp.session.setSegments([planInfo.plan]);
      Crisp.session.setData({
        billing_summary: billingSummary,
        plan: planInfo.plan,
        usage_count: planInfo.usageCount,
        usage_limit: planInfo.usageLimit ?? "unlimited",
        subscription_status: subscriptionStatusLabel,
      });
    } catch (err) {
      console.error("[CrispWidget] Failed to set plan data on session:", err);
    }
  }, [isSignedIn, planInfo]);

  return null;
}
